<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name: 'sketch', position: 'top', allowOtherLeft: true }"
    :button-bind="{ icon, tooltip }"
    :loading="loading"
    unfill-width
  >
    <template #content>
      <div class="tw:p-2.5 tw:flexrow-1.5 tw:items-stretch tw:[&_.swatchwrapper]:h-auto">
        <div class="tw:flexcenter">
          <!-- DRAWING BUTTON -->
          <base-button
            icon="mdi-brush"
            type="text"
            :color="active ? 'primary' : undefined"
            :disabled="!visible"
            @click="active = !active"
          />
        </div>

        <!-- COLOR PICKER -->
        <div class="tw:flexcenter">
          <data-input v-model="color" :data-type="{ name: 'color' }" />
        </div>

        <!-- STROKE WIDTH NUMERIC INPUT -->
        <div class="tw:flexcenter">
          <div class="tw:max-w-[50px]">
            <data-input
              :value="stroke"
              @change="$event => (stroke = parseInt($event))"
              :data-type="{ name: 'integer' }"
              class="tw:h-[25px] tw:[&_.v-input\_\_slot]:h-[25px] tw:[&_.v-input\_\_slot]:min-h-[25px]"
              :min-value="1"
              :max-value="10"
              hide-arrows
              hide-details
            />
          </div>
        </div>

        <!-- STROKE WIDTH SLIDER INPUT -->
        <div class="tw:flexcenter">
          <div class="tw:max-w-[150px] tw:w-[150px] tw:flexcenter">
            <data-input
              v-model="stroke"
              class="tw:grow tw:[&_>_div_>_div]:pt-0 tw:[&_.v-input\_\_slot]:p-0"
              :data-type="{ name: 'slider' }"
              :min="1"
              :max="10"
              hide-value-label
              hide-details
            />
          </div>
        </div>

        <!-- DIVIDER -->
        <div class="tw:bg-gray-200 tw:w-[1px]"></div>

        <!-- ACTION BUTTONS -->
        <div class="tw:flexcenter">
          <!-- VISIBILITY -->
          <base-button
            type="text"
            :color="visible ? 'primary' : undefined"
            :icon="visible ? 'mdi-eye' : 'mdi-eye-off'"
            :loading="refreshingTarget === 'visibility'"
            :disabled="refreshingTarget && refreshingTarget !== 'visibility'"
            @click="visible = !visible"
          />

          <!-- REFRESH -->
          <base-button
            type="text"
            icon="mdi-refresh"
            :loading="refreshingTarget === 'refreshing'"
            :disabled="refreshingTarget && refreshingTarget !== 'refreshing'"
            @click="redrawSketches('refreshing')"
          />

          <!-- MENU CLOSE -->
          <base-button type="text" icon="mdi-close" @click="isMenuOpen = false" />
        </div>
      </div>

      <!-- MAP POPOVER -->
      <sketch-popover
        v-if="identificationData"
        :coordinates="identificationData?.coordinates"
        :feature="identificationData?.feature"
        @onRemoved="identificationData = null"
      />
    </template>
  </navbar-menu-tool>
</template>

<script>
import NavbarMenuTool from '@/components/NavbarMenuTool';
import DataInput from '@/components/DataInput';
import SketchPopover from '@/components/SketchPopover';

import { call, get } from 'vuex-pathify';

export default {
  name: 'TheNavbarToolsSketch',
  components: {
    NavbarMenuTool,
    DataInput,
    SketchPopover,
  },
  props: {
    icon: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
  },
  data: () => ({
    isMenuOpen: false,
    loading: false,

    active: true,
    visible: true,
    refreshingTarget: null,

    color: '#000',
    stroke: 2,

    drawings: [],

    identificationData: null,
  }),
  computed: {
    currentProjectId: get('layers/project.id'),
    style() {
      return { color: this.color, stroke: this.stroke };
    },
  },
  watch: {
    /** Save currently changed style to localstorage */
    style(style) {
      localStorage.setItem('sketches-brush-color', style.color);
      localStorage.setItem('sketches-brush-stroke', style.stroke);
      this.$root.$emit('changeSketchStyle', style);
    },

    /** Action on menu close and open */
    isMenuOpen(isOpened) {
      if (isOpened) {
        this.switchActiveTool();
      } else {
        this.identificationData = null;
        this.$root.$emit('sketch-action', false);
        this.$root.$emit('sketchIdentification-action', false);
      }
    },

    /** Visibility manipulation */
    visible(isVisible) {
      if (!this.isMenuOpen) return;
      this.handleVisibilityLocalStorage();
      if (isVisible) {
        if (!this.active) this.$root.$emit('sketchIdentification-action', true, this.onSketchIdentified);
        this.redrawSketches('visibility');
      } else {
        this.active = false;
        this.identificationData = null;
        this.$root.$emit('toggleSketchLayer');
      }
    },

    /** Drawing state manipulation */
    active(isActive) {
      if (!this.isMenuOpen) return;
      if (isActive) localStorage.removeItem('sketches-drawing-deactivated');
      else localStorage.setItem('sketches-drawing-deactivated', 'true');
      this.switchActiveTool();
    },
  },
  methods: {
    getProjectSketches: call('sketch/getProjectSketches'),
    async redrawSketches(target) {
      this.refreshingTarget = target ?? 'none';
      this.identificationData = null;
      try {
        this.drawings = await this.getProjectSketches(this.currentProjectId);
        this.$root.$emit('toggleSketchLayer', this.drawings ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        this.refreshingTarget = null;
      }
    },
    handleVisibilityLocalStorage() {
      const projectsWithHiddenSketches = JSON.parse(localStorage.getItem('sketches-hidden-projects') ?? '[]');
      if (this.visible) {
        const updatedProjects = projectsWithHiddenSketches.filter(projectId => projectId !== this.currentProjectId);
        if (!updatedProjects.length) localStorage.removeItem('sketches-hidden-projects');
        else localStorage.setItem('sketches-hidden-projects', JSON.stringify(updatedProjects));
      } else {
        localStorage.setItem(
          'sketches-hidden-projects',
          JSON.stringify([...projectsWithHiddenSketches, this.currentProjectId])
        );
      }
    },

    /** If menu is opened and sketches are visible on the map, there should be either identification or drawing enabled */
    switchActiveTool() {
      this.identificationData = null;
      if (this.active) {
        this.$root.$emit('sketch-action', true, {
          color: this.color,
          stroke: this.stroke,
        });
      } else if (this.visible) {
        this.$root.$emit('sketchIdentification-action', true, this.onSketchIdentified);
      } else {
        this.$root.$emit('sketch-action', false);
        this.$root.$emit('sketchIdentification-action', false);
      }
    },
    onSketchIdentified(data) {
      this.identificationData = data.feature ? data : null;
    },

    /** Initial logic */
    setInitialVisibility() {
      const projectsWithHiddenSketches = JSON.parse(localStorage.getItem('sketches-hidden-projects') ?? '[]');
      const sketchesHidden = !!projectsWithHiddenSketches.includes(this.currentProjectId);
      this.visible = !sketchesHidden;
      if (this.visible) this.redrawSketches();
    },
    setInitialDrawingState() {
      const drawingDeactivated = !!localStorage.getItem('sketches-drawing-deactivated');
      this.active = drawingDeactivated ? false : !!this.visible;
    },
    setInitialStyle() {
      const brushColor = localStorage.getItem('sketches-brush-color');
      if (brushColor) this.color = brushColor;
      const brushStroke = parseInt(localStorage.getItem('sketches-brush-stroke'));
      if (brushStroke) this.stroke = brushStroke;
    },
  },
  beforeDestroy() {
    this.$root.$emit('toggleSketchLayer');
    this.$root.$emit('sketch-action', false);
    this.$root.$emit('sketchIdentification-action', false);
  },
  created() {
    /** Get visibility from localstorage and parse drawings if it is not turned off */
    this.setInitialVisibility();

    /** Get drawing active state from localstorage  */
    this.setInitialDrawingState();

    /** Get draw parameters from localstorage */
    this.setInitialStyle();
  },
};
</script>
